import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';

export interface User {
  userId: string;
  email: string;
  givenName?: string;
  familyName?: string;
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, givenName: string, familyName: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  verifyAccount: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshAuthState: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      
      if (currentUser) {
        console.log('Current user data:', currentUser);
        
        // Get user attributes to extract proper user information
        const { fetchUserAttributes } = await import('aws-amplify/auth');
        const userAttributes = await fetchUserAttributes();
        console.log('User attributes:', userAttributes);
        
        // Extract user information from the Amplify user object
        const userInfo: User = {
          userId: currentUser.userId,
          email: userAttributes.email || currentUser.signInDetails?.loginId || currentUser.username || '',
          givenName: userAttributes.given_name || undefined,
          familyName: userAttributes.family_name || undefined,
          emailVerified: userAttributes.email_verified === 'true' || true, // Assume verified if they can sign in
        };
        
        console.log('Setting user info:', userInfo);
        setUser(userInfo);
      }
    } catch (error) {
      console.log('No authenticated user found:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if there's already a signed-in user and sign them out first
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          await signOut();
        }
      } catch (err) {
        // No current user, which is expected for a fresh login
      }
      
      // Ensure Amplify is properly configured (this was working)
      const { Amplify } = await import('aws-amplify');
      const { amplifyConfig } = await import('../config/env');
      Amplify.configure(amplifyConfig);
      
      // Verify configuration
      const config = Amplify.getConfig();
      console.log('Amplify configured with:', {
        userPoolId: config.Auth?.Cognito?.userPoolId,
        userPoolClientId: config.Auth?.Cognito?.userPoolClientId,
        region: config.Auth?.Cognito?.region
      });
      
      const { signIn } = await import('aws-amplify/auth');
      
      const result = await signIn({
        username: email,
        password: password,
      });

      if (result.isSignedIn) {
        await checkAuthState();
      } else {
        // Handle cases where additional steps might be required
        if (result.nextStep) {
          setError(`Additional verification required: ${result.nextStep.signInStep}`);
        }
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string, 
    password: string, 
    givenName: string, 
    familyName: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const { signUp } = await import('aws-amplify/auth');
      const result = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            given_name: givenName,
            family_name: familyName,
          },
        },
      });

      console.log('Sign up result:', result);
      
      // Check if email verification is required
      if (!result.isSignUpComplete && result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        // User needs to verify their email
        throw { name: 'UserNotConfirmedException', message: 'Please verify your email address' };
      } else if (result.isSignUpComplete) {
        // Registration completed successfully, user can sign in
        console.log('Registration completed successfully');
      } else {
        // Handle other cases
        console.log('Unexpected sign up result:', result);
        throw new Error('Registration completed but in an unexpected state');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      
      // Don't set error for UserNotConfirmedException as it should trigger verification flow
      if (err.name !== 'UserNotConfirmedException') {
        setError(getErrorMessage(err));
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      await signOut();
      setUser(null);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: email });
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmResetPassword = async (
    email: string, 
    code: string, 
    newPassword: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });
    } catch (err: any) {
      console.error('Confirm reset password error:', err);
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (session.tokens) {
        // Token refreshed successfully
        console.log('Token refreshed successfully');
      }
    } catch (err: any) {
      console.error('Token refresh error:', err);
      // If token refresh fails, user needs to login again
      setUser(null);
      throw err;
    }
  };

  const refreshAuthState = async (): Promise<void> => {
    await checkAuthState();
  };

  const clearError = () => {
    setError(null);
  };

  const getErrorMessage = (error: any): string => {
    if (error.name === 'UserNotConfirmedException') {
      return 'Please check your email and confirm your account before signing in.';
    }
    if (error.name === 'NotAuthorizedException') {
      return 'Invalid email or password. Please try again.';
    }
    if (error.name === 'UserNotFoundException') {
      return 'No account found with this email address.';
    }
    if (error.name === 'UsernameExistsException') {
      return 'An account with this email already exists.';
    }
    if (error.name === 'InvalidPasswordException') {
      return 'Password does not meet requirements. Please choose a stronger password.';
    }
    if (error.name === 'LimitExceededException') {
      return 'Too many attempts. Please try again later.';
    }
    if (error.name === 'CodeMismatchException') {
      return 'Invalid verification code. Please try again.';
    }
    if (error.name === 'ExpiredCodeException') {
      return 'Verification code has expired. Please request a new one.';
    }
    if (error.message && error.message.includes('already a signed in user')) {
      return 'You are already signed in. Please refresh the page or try again.';
    }
    
    return error.message || 'An unexpected error occurred. Please try again.';
  };

  const verifyAccount = async (email: string, code: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const { confirmSignUp } = await import('aws-amplify/auth');
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      
      // After successful verification, check if the user is automatically signed in
      // Some Cognito configurations automatically sign users in after verification
      try {
        await checkAuthState();
        console.log('User automatically signed in after verification');
      } catch (err) {
        console.log('User not automatically signed in after verification, manual login required');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const { resendSignUpCode } = await import('aws-amplify/auth');
      await resendSignUpCode({
        username: email,
      });
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    resetPassword,
    confirmResetPassword,
    verifyAccount,
    resendVerificationCode,
    refreshToken,
    refreshAuthState,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};